import { FC, useEffect, useState } from "react";
import { Modal, Form, Row, Col, Button } from "react-bootstrap";
import { Debit } from "@models/debit.model";
import { CreateDebitPayload } from "@services/finance/debits/commands/cmdCreateDebit";
import { UpdateDebitPayload } from "@services/finance/debits/commands/cmdUpdateDebit";

interface Props {
  show: boolean;
  onClose: () => void;
  onSave: (payload: CreateDebitPayload | UpdateDebitPayload) => void;
  debit: Debit | null;
  isSaving: boolean;
}

interface FormState {
  amount: string;
  effective_date: string;
  reporter: string;
  reason: string;
}

const EMPTY: FormState = { amount: "", effective_date: "", reporter: "", reason: "" };

const DebitFormModal: FC<Props> = ({ show, onClose, onSave, debit, isSaving }) => {
  const isEdit = debit !== null;
  const [form, setForm] = useState<FormState>(EMPTY);

  useEffect(() => {
    if (!show) return;
    if (debit) {
      setForm({
        amount: String(debit.amount ?? ""),
        effective_date: debit.effective_date ?? "",
        reporter: debit.reporter ?? "",
        reason: debit.reason ?? "",
      });
    } else {
      setForm(EMPTY);
    }
  }, [debit, show]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((p) => ({ ...p, [key]: value }));

  const handleSave = () => {
    const common = {
      amount: parseFloat(form.amount) || 0,
      effective_date: form.effective_date || null,
      reporter: form.reporter.trim() || null,
      reason: form.reason.trim() || null,
      source_type: "MANUAL",
    };
    onSave(common satisfies CreateDebitPayload satisfies UpdateDebitPayload);
  };

  const canSubmit = form.amount !== "" && !isNaN(parseFloat(form.amount));

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">{isEdit ? "Edit Debit" : "Add Debit"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">
                  Amount <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.amount}
                  onChange={(e) => update("amount", e.target.value)}
                  placeholder="0.00"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Effective Date</Form.Label>
                <Form.Control
                  type="date"
                  value={form.effective_date}
                  onChange={(e) => update("effective_date", e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Reporter</Form.Label>
            <Form.Control
              type="text"
              value={form.reporter}
              onChange={(e) => update("reporter", e.target.value)}
              placeholder="Optional"
            />
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label className="fw-semibold">Reason</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.reason}
              onChange={(e) => update("reason", e.target.value)}
              placeholder="Optional note"
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={isSaving || !canSubmit}>
          {isSaving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" /> Saving...
            </>
          ) : (
            <>
              <i className="bi bi-check-circle me-2" />
              {isEdit ? "Save Changes" : "Add Debit"}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DebitFormModal;
